// ─── Types ───────────────────────────────────────────────────────────

export type ShortlistStatus = 'new' | 'contacted' | 'interviewing' | 'offered' | 'rejected';
export type CandidateAvailability = 'immediate' | '2weeks' | '1month' | '3months';

export interface CandidateItem {
  id: string;
  name: string;
  initials: string;
  title: string;
  location: string;
  match: number;
  experience: string;
  availability: CandidateAvailability;
  skills: string[];
  source: string;
  timeAgo: string;
  bio: string;
}

export interface ShortlistedCandidate {
  id: string;
  name: string;
  initials: string;
  title: string;
  location: string;
  experience: string;
  availability: CandidateAvailability;
  status: ShortlistStatus;
  shortlistedAgo: string;
}

// ─── Colors ─────────────────────────────────────────────────────────

export const SHORTLIST_STATUS_COLORS: Record<ShortlistStatus, string> = {
  new: '#007AFF',
  contacted: '#FF9500',
  interviewing: '#34C759',
  offered: '#542DFF',
  rejected: '#FF3B30',
};

// ─── Filter options ─────────────────────────────────────────────────

export interface FilterOption {
  value: string;
  labelKey: string;
}

export interface FilterGroup {
  id: string;
  labelKey: string;
  options: FilterOption[];
}

export const CANDIDATE_FILTER_GROUPS: FilterGroup[] = [
  {
    id: 'experience',
    labelKey: 'candidates.filters.experience',
    options: [
      { value: '0-2', labelKey: 'candidates.filters.junior' },
      { value: '3-5', labelKey: 'candidates.filters.mid' },
      { value: '6-10', labelKey: 'candidates.filters.senior' },
      { value: '10+', labelKey: 'candidates.filters.executive' },
    ],
  },
  {
    id: 'location',
    labelKey: 'candidates.filters.location',
    options: [
      { value: 'Abu Dhabi', labelKey: 'candidates.filters.abuDhabi' },
      { value: 'Dubai', labelKey: 'candidates.filters.dubai' },
      { value: 'Sharjah', labelKey: 'candidates.filters.sharjah' },
      { value: 'Remote', labelKey: 'candidates.filters.remote' },
    ],
  },
  {
    id: 'availability',
    labelKey: 'candidates.filters.availability',
    options: [
      { value: 'immediate', labelKey: 'candidates.filters.immediate' },
      { value: '2weeks', labelKey: 'candidates.filters.twoWeeks' },
      { value: '1month', labelKey: 'candidates.filters.oneMonth' },
      { value: '3months', labelKey: 'candidates.filters.threeMonths' },
    ],
  },
];

// ─── Mock data ──────────────────────────────────────────────────────

export const CANDIDATES: CandidateItem[] = [
  {
    id: '1',
    name: 'Mariam Al Zaabi',
    initials: 'MA',
    title: 'Senior HR Manager',
    location: 'Abu Dhabi',
    match: 94,
    experience: '8 years',
    availability: 'immediate',
    skills: ['HR Strategy', 'Talent Management', 'Employee Relations', 'Compensation & Benefits'],
    source: 'Tabbio',
    timeAgo: '1h',
    bio: 'Experienced HR leader with 8 years in the energy and aviation sectors across the GCC. Specializes in talent management, organizational development, and building high-performance HR teams. Currently leading HR operations for a 200+ workforce.',
  },
  {
    id: '2',
    name: 'Ahmed Rashid',
    initials: 'AR',
    title: 'Talent Acquisition Director',
    location: 'Dubai',
    match: 91,
    experience: '10 years',
    availability: '2weeks',
    skills: ['Recruitment', 'Employer Branding', 'ATS', 'Headcount Planning', 'Executive Search'],
    source: 'LinkedIn',
    timeAgo: '3h',
    bio: 'Talent acquisition expert with a decade of experience building recruitment functions for Fortune 500 companies in the Middle East. Led teams of 20+ recruiters and managed annual hiring volumes of 500+ positions across multiple jurisdictions.',
  },
  {
    id: '3',
    name: 'Noura Khalifa',
    initials: 'NK',
    title: 'People & Culture VP',
    location: 'Abu Dhabi',
    match: 89,
    experience: '12 years',
    availability: '1month',
    skills: ['Culture Transformation', 'Change Management', 'Leadership Development', 'DEI'],
    source: 'Tabbio',
    timeAgo: '5h',
    bio: 'Seasoned people leader who has driven culture transformation programs at three major UAE enterprises. Expert in change management, leadership development, and building inclusive workplaces. Holds SHRM-SCP certification.',
  },
  {
    id: '4',
    name: 'Khalid Saeed',
    initials: 'KS',
    title: 'HR Business Partner',
    location: 'Dubai',
    match: 86,
    experience: '6 years',
    availability: 'immediate',
    skills: ['HRBP', 'Performance Management', 'Employee Engagement', 'HR Analytics'],
    source: 'Bayt',
    timeAgo: '1d',
    bio: 'Strategic HRBP partnering with engineering and product teams at a leading UAE tech company. Drives performance management cycles, engagement surveys, and data-driven people initiatives for a 300-person product organization.',
  },
  {
    id: '5',
    name: 'Fatima Al Suwaidi',
    initials: 'FS',
    title: 'Learning & Development Head',
    location: 'Abu Dhabi',
    match: 83,
    experience: '7 years',
    availability: '3months',
    skills: ['L&D Strategy', 'Training Design', 'Succession Planning', 'Coaching'],
    source: 'Tabbio',
    timeAgo: '2d',
    bio: 'L&D specialist who has designed and delivered enterprise learning programs across the GCC. Built a leadership academy serving 150+ managers and implemented succession planning frameworks for critical roles.',
  },
];

export const SHORTLISTED: ShortlistedCandidate[] = [
  {
    id: '1',
    name: 'Mariam Al Zaabi',
    initials: 'MA',
    title: 'Senior HR Manager',
    location: 'Abu Dhabi',
    experience: '8 years',
    availability: 'immediate',
    status: 'interviewing',
    shortlistedAgo: '2d ago',
  },
  {
    id: '2',
    name: 'Ahmed Rashid',
    initials: 'AR',
    title: 'Talent Acquisition Director',
    location: 'Dubai',
    experience: '10 years',
    availability: '2weeks',
    status: 'contacted',
    shortlistedAgo: '3d ago',
  },
  {
    id: '3',
    name: 'Noura Khalifa',
    initials: 'NK',
    title: 'People & Culture VP',
    location: 'Abu Dhabi',
    experience: '12 years',
    availability: '1month',
    status: 'new',
    shortlistedAgo: '5d ago',
  },
  {
    id: '4',
    name: 'Khalid Saeed',
    initials: 'KS',
    title: 'HR Business Partner',
    location: 'Dubai',
    experience: '6 years',
    availability: 'immediate',
    status: 'rejected',
    shortlistedAgo: '1w ago',
  },
];
